<template>
  <!-- Shortcut Badge (always visible at bottom-right) -->
  <Transition name="dev-badge">
    <div v-if="devMode" id="dev-mode-overlay" class="dev-shortcut-badge active">
      <span class="dev-badge-icon">&lt;/&gt;</span>
      <span class="dev-badge-label">DEV MODE</span>
      <span class="dev-badge-shortcut">Ctrl+Shift+D to exit</span>
    </div>
  </Transition>

  <!-- Highlight Box -->
  <template v-if="devMode && hoveredEl.rect">
    <!-- Margin layer -->
    <div
      class="dev-highlight dev-highlight--margin"
      :style="{
        top: (hoveredEl.rect.top - hoveredEl.marginBox[0]) + 'px',
        left: (hoveredEl.rect.left - hoveredEl.marginBox[3]) + 'px',
        width: (hoveredEl.rect.width + hoveredEl.marginBox[1] + hoveredEl.marginBox[3]) + 'px',
        height: (hoveredEl.rect.height + hoveredEl.marginBox[0] + hoveredEl.marginBox[2]) + 'px'
      }"
    ></div>
    <!-- Padding layer -->
    <div
      class="dev-highlight dev-highlight--padding"
      :style="{
        top: hoveredEl.rect.top + 'px',
        left: hoveredEl.rect.left + 'px',
        width: hoveredEl.rect.width + 'px',
        height: hoveredEl.rect.height + 'px'
      }"
    ></div>
    <!-- Content layer -->
    <div
      class="dev-highlight dev-highlight--content"
      :style="{
        top: (hoveredEl.rect.top + hoveredEl.paddingBox[0]) + 'px',
        left: (hoveredEl.rect.left + hoveredEl.paddingBox[3]) + 'px',
        width: (hoveredEl.rect.width - hoveredEl.paddingBox[1] - hoveredEl.paddingBox[3]) + 'px',
        height: (hoveredEl.rect.height - hoveredEl.paddingBox[0] - hoveredEl.paddingBox[2]) + 'px'
      }"
    ></div>

    <!-- Element Name Label (floating above the element) -->
    <div
      class="dev-element-label"
      :style="labelStyle"
    >
      <span v-if="hoveredEl.componentName" class="dev-label-component">{{ hoveredEl.componentName }}</span>
      <span class="dev-label-tag">{{ hoveredEl.tag }}</span>
      <span v-if="hoveredEl.id" class="dev-label-id">{{ hoveredEl.id }}</span>
      <span v-if="hoveredEl.classes" class="dev-label-classes">{{ truncate(hoveredEl.classes, 30) }}</span>
      <span class="dev-label-size">{{ hoveredEl.size }}</span>
    </div>

    <!-- Tooltip -->
    <div
      class="dev-mode-tooltip"
      :style="tooltipStyle"
    >
      <div class="dev-tooltip-header">
        <span v-if="hoveredEl.componentName" class="dev-component-badge">{{ hoveredEl.componentName }}</span>
        <span class="dev-tag">{{ hoveredEl.tag }}</span>
        <span v-if="hoveredEl.id" class="dev-id">{{ hoveredEl.id }}</span>
        <span v-if="hoveredEl.classes" class="dev-classes">{{ truncate(hoveredEl.classes, 40) }}</span>
      </div>
      <div class="dev-tooltip-grid">
        <div class="dev-prop">
          <span class="dev-prop-label">size</span>
          <span class="dev-prop-value">{{ hoveredEl.size }}</span>
        </div>
        <div class="dev-prop">
          <span class="dev-prop-label">display</span>
          <span class="dev-prop-value">{{ hoveredEl.display }}</span>
        </div>
        <div class="dev-prop">
          <span class="dev-prop-label">position</span>
          <span class="dev-prop-value">{{ hoveredEl.position }}</span>
        </div>
        <div class="dev-prop">
          <span class="dev-prop-label">padding</span>
          <span class="dev-prop-value">{{ hoveredEl.padding }}</span>
        </div>
        <div class="dev-prop">
          <span class="dev-prop-label">margin</span>
          <span class="dev-prop-value">{{ hoveredEl.margin }}</span>
        </div>
        <div class="dev-prop">
          <span class="dev-prop-label">font</span>
          <span class="dev-prop-value">{{ truncate(hoveredEl.font, 30) }}</span>
        </div>
      </div>
      <div class="dev-tooltip-footer">
        <span class="dev-click-hint">&#9654; click element to copy name</span>
      </div>
      <div class="dev-tooltip-legend">
        <span class="legend-item"><span class="legend-box margin"></span>margin</span>
        <span class="legend-item"><span class="legend-box padding"></span>padding</span>
        <span class="legend-item"><span class="legend-box content"></span>content</span>
      </div>
    </div>
  </template>

  <!-- Copied Toast -->
  <Transition name="dev-toast">
    <div
      v-if="copiedText"
      class="dev-copied-toast"
      :style="{ top: (copiedPos.y - 40) + 'px', left: copiedPos.x + 'px' }"
    >
      <span class="dev-copied-check">&#10003;</span>
      <span class="dev-copied-label">Copied!</span>
      <span class="dev-copied-selector">{{ truncate(copiedText, 40) }}</span>
    </div>
  </Transition>
</template>

<script setup>
import { computed } from 'vue'
import { useDevMode } from '../composables/useDevMode'

const { devMode, hoveredEl, copiedText, copiedPos } = useDevMode()

function truncate(str, max) {
  return str.length > max ? str.slice(0, max) + '...' : str
}

const labelStyle = computed(() => {
  if (!hoveredEl.rect) return {}

  const gap = 4
  let top = hoveredEl.rect.top - 24 - gap
  let left = hoveredEl.rect.left

  if (top < 4) {
    top = hoveredEl.rect.top + hoveredEl.rect.height + gap
  }
  if (left < 4) left = 4

  return { top: top + 'px', left: left + 'px' }
})

const tooltipStyle = computed(() => {
  if (!hoveredEl.rect) return {}

  const tooltipW = 300
  const tooltipH = 200
  const gap = 10

  let top = hoveredEl.rect.top + hoveredEl.rect.height + gap
  let left = hoveredEl.rect.left

  if (top + tooltipH > window.innerHeight) {
    top = hoveredEl.rect.top - tooltipH - gap
  }
  if (left + tooltipW > window.innerWidth) {
    left = window.innerWidth - tooltipW - gap
  }
  if (left < gap) left = gap
  if (top < gap) top = gap

  return { top: top + 'px', left: left + 'px' }
})
</script>
